"use client";

import { Button, Text, VStack } from "@chakra-ui/react";
import { z } from "zod";
import GenericIcon from "@/shared/ui/actions/GenericIcon";
import GenericDrawerInfo from "@/shared/ui/display/GenericDrawerInfo";
import GenericUploadFile from "@/shared/ui/GenericUploadFile";
import {
  SupportedLanguage,
  SupportedResumeExtensions,
} from "@/generated/fastapi";
import { useState } from "react";
import { type FileChangeDetails } from "@zag-js/file-upload";
import {
  showErrorToast,
  showInfoToast,
  showSuccessToast,
  showWarningToast,
} from "@/shared/feedback";
import { actRevalidatePrivateLayout } from "@/app/private-shell";
import {
  PROFILE_SHAPING_LABELS,
  PROFILE_SHAPING_LOADING_STATES,
} from "@/features/profile-shaping";
import useParseResumeMutation from "../mutations/useParseResumeMutation";

// Define the mapping between MIME types and file extensions supported by backend
const FILE_TYPE_MAP: Record<string, SupportedResumeExtensions> = {
  "application/pdf": ".pdf",
  "text/plain": ".txt",
  "text/markdown": ".md",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document":
    ".docx",
};

// Define allowed file types using the keys of the mapping
const ALLOWED_FILE_TYPES = Object.keys(FILE_TYPE_MAP);

// Generate the string of allowed file extensions
const ALLOWED_FILE_EXTENSIONS = Object.values(FILE_TYPE_MAP);

// Create the display string for the UI
const ALLOWED_FILE_TYPES_DISPLAY = ALLOWED_FILE_EXTENSIONS.join(", ");
const ALLOWED_FILE_UPLOAD_ACCEPT = ALLOWED_FILE_EXTENSIONS.join(",");

// Max file size in bytes (5MB)
const MAX_FILE_SIZE = 5 * 1024 * 1024;

const FileSchema = z.object({
  name: z.string(),
  size: z
    .number()
    .max(MAX_FILE_SIZE, { message: "File size must be less than 5MB" }),
  type: z.string(),
});

type FileValidationResult = {
  valid: boolean;
  errors: string[] | null;
};

interface UploadResumeFormProps {
  preferredLanguage: SupportedLanguage | null;
  onSuccessParsing?: () => void; // Callback function on successful resume parsing
}

export default function UploadResumeForm({
  preferredLanguage,
  onSuccessParsing,
}: UploadResumeFormProps) {
  const parseResumeMutation = useParseResumeMutation();
  const [parsedResumeError, setParsedResumeError] = useState<string | null>(
    null,
  );

  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [fileKeyTrigger, setFileKeyTrigger] = useState<number>(0);
  const uploadDisabled =
    parseResumeMutation.isPending || !preferredLanguage || !selectedFile;
  const uploadDisabledReason =
    selectedFile && !preferredLanguage
      ? "File ready. Choose a preferred language above to enable shaping from source."
      : !selectedFile
        ? "Choose a CV or resume file before shaping from source."
        : !preferredLanguage
          ? "Choose a preferred language before shaping from source."
          : null;
  const uploadDisabledReasonColor =
    selectedFile && !preferredLanguage ? "moveon.warning" : "moveon.text.muted";

  // Helpers
  function handleClearFileSelection() {
    setSelectedFile(null);
    setFileKeyTrigger((current) => current + 1);
  }

  function handleFileChange(details: FileChangeDetails) {
    // Only process accepted files
    if (details.acceptedFiles.length > 0) {
      const file = details.acceptedFiles[0];

      // Validate the file type using our Zod schema
      const validation = validateFile(file);

      if (validation.valid) {
        setSelectedFile(file);
        setParsedResumeError(null);
      } else {
        const errorMessage =
          validation.errors?.join(". ") ||
          "Invalid file type. Please upload a text document (PDF, TXT, DOCX, etc.)";
        setParsedResumeError(errorMessage);
        showErrorToast(errorMessage);
        handleClearFileSelection(); // Clear the selection if validation fails
      }
    } else {
      setSelectedFile(null);
    }

    // Log rejected files
    if (details.rejectedFiles.length > 0) {
      showWarningToast("Some files were rejected. Please check the file type.");
    }
  }

  // Validation
  function validateFile(file: File): FileValidationResult {
    try {
      FileSchema.parse(file);
      const lowerFileName = file.name.toLowerCase();
      const hasAllowedExtension = ALLOWED_FILE_EXTENSIONS.some((extension) =>
        lowerFileName.endsWith(extension),
      );
      const hasAllowedMimeType =
        file.type.length > 0 && ALLOWED_FILE_TYPES.includes(file.type);

      if (!hasAllowedExtension && !hasAllowedMimeType) {
        return {
          valid: false,
          errors: [
            `Only these file formats are allowed: ${ALLOWED_FILE_TYPES_DISPLAY}`,
          ],
        };
      }

      return { valid: true, errors: null };
    } catch (error) {
      if (error instanceof z.ZodError) {
        return {
          valid: false,
          errors: error.errors.map((e) => e.message),
        };
      }
      return {
        valid: false,
        errors: ["Unknown validation error"],
      };
    }
  }

  async function handleResumeUploadClick(selectedFile: File | null) {
    if (!selectedFile) {
      setParsedResumeError("No file selected");
      showWarningToast("No file selected");
      return;
    }

    if (!preferredLanguage) {
      showWarningToast("Choose a preferred language before shaping from source.");
      return;
    }

    // Double-check validation before processing
    const validation = validateFile(selectedFile);
    if (!validation.valid) {
      const errorMessage =
        validation.errors?.join(". ") ||
        "Invalid file type. Please upload a text document.";
      setParsedResumeError(errorMessage);
      showErrorToast(errorMessage);
      return;
    }

    try {
      const response = await parseResumeMutation.mutateAsync({
        preferredLanguage,
        selectedFile,
      });

      // Handle success response
      if (response.status === "success") {
        const userMessage = `Source material shaped: found ${response.identified_situations_education} education situations and ${response.identified_situations_occupation} work situations, with ${response.identified_thoughts} suggested thoughts.`;
        showSuccessToast(userMessage);

        handleClearFileSelection();
        await actRevalidatePrivateLayout(); // revalidate the private layout to load situations date server-side  -- critical for new accounts, keep it here by default even if only parsing resume for existing accounts

        setParsedResumeError(null); // Clear any previous errors
        onSuccessParsing?.(); // Callback OnSuccess if provided
      }
    } catch (error) {
      if (error instanceof Error) {
        setParsedResumeError(`CV upload failed: ${error.message}`);
        showErrorToast(`CV upload failed: ${error.message}`);
      } else {
        setParsedResumeError("CV upload failed with unknown error");
        showErrorToast("CV upload failed with unknown error");
      }
      showInfoToast(
        "Try again same file or different format. If issue persists, contact support.",
      );
      handleClearFileSelection();
    }
  }

  return (
    <VStack align="stretch" gap="4" minW="0" w="full">
      <GenericDrawerInfo
        w="full"
        minW="0"
        description="Upload an existing CV or resume so MoveOn can keep it as source material and suggest career situations and thoughts from it."
        items={[
          { label: ALLOWED_FILE_TYPES_DISPLAY, colorPalette: "blue" },
          {
            label: selectedFile ? selectedFile.name : "No file selected",
            colorPalette: selectedFile ? "green" : "gray",
          },
        ]}
      />

      <GenericUploadFile
        accept={ALLOWED_FILE_UPLOAD_ACCEPT}
        fileInputKey={fileKeyTrigger}
        helperText={`Supported formats: ${ALLOWED_FILE_TYPES_DISPLAY}.`}
        selectedFile={selectedFile}
        validationMessage={parsedResumeError}
        onClear={handleClearFileSelection}
        onFileChange={handleFileChange}
      />

      {uploadDisabledReason ? (
        <Text fontSize="sm" color={uploadDisabledReasonColor}>
          {uploadDisabledReason}
        </Text>
      ) : null}

      {/* Upload button */}

      <Button
        onClick={() => handleResumeUploadClick(selectedFile)}
        disabled={uploadDisabled}
        loading={parseResumeMutation.isPending}
        loadingText={PROFILE_SHAPING_LOADING_STATES.extraction.actionLabel}
        bg="moveon.primary"
        alignSelf={{ base: "stretch", sm: "flex-end" }}
      >
        {PROFILE_SHAPING_LABELS.extractionPhase}
        <GenericIcon icon="upload" />
      </Button>
    </VStack>
  );
}
